import * as THREE from "three";
import { IController } from "./core/types/controller_types.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { SimulationContext } from "../simulation/simulation_context.js";
import { VfxTaskRunner } from "./core/runtime/vfx_task_runner.js";
import { FpsOverlay } from "../debug/fps_overlay.js";
import { VfxBallBlockImpactTask, BlockImpactMaterial } from "./effects/ball_impact/vfx_ball_block_impact_task.js";
import { VfxBigBallBlockImpactTask } from "./effects/ball_impact/vfx_big_ball_block_impact_task.js";
import { VfxTableImpactTask } from "./effects/ball_impact/vfx_table_impact_task.js";
import { VfxBlockShatterTask } from "./effects/block_shatter/vfx_block_shatter_task.js";
import { VfxFireworkExplosionTask } from "./effects/fireworks/firework_explosion/vfx_firework_explosion_task.js";
import { VfxRocketTrailTask } from "./effects/fireworks/firework_trail/vfx_firework_trail_task.js";
import { VfxJamSpillTask } from "./effects/jam/vfx_jam_spill_task.js";
import { VfxJamSplashTask } from "./effects/jam/vfx_jam_splash_task.js";
import { VfxCannonFireTask } from "./effects/vfx_cannon_fire/vfx_cannon_fire_task.js";
import { VfxIceSplintersTask, IceSplinterType } from "./effects/ice/vfx_ice_splinters_task.js";
import { VfxWatermelonBreakTask } from "./effects/watermelon/vfx_watermelon_break_task.js";

export class Vfx3DManager
{
  public static suppress_cannon_ball_impact = false;

  public static addCannonFire( position: THREE.Vector3, direction: THREE.Vector3 ): void
  {
    FpsOverlay.noteEvent( "shot" );
    VfxTaskRunner.addTask( new VfxCannonFireTask( position, direction ) );
  }

  public static addCannonBallTableImpact( position: THREE.Vector3 ): void
  {
    if ( SimulationContext.enabled || this.suppress_cannon_ball_impact ) return;

    // Table hit (cannon ball)
    VfxTaskRunner.addTask( new VfxTableImpactTask( position ) );
  }

  public static addCannonBallBlockImpact(
    position: THREE.Vector3,
    material: BlockImpactMaterial = BlockImpactMaterial.STONE
  ): void
  {
    if ( SimulationContext.enabled || this.suppress_cannon_ball_impact ) return;

    // Block hit (cannon ball)
    VfxTaskRunner.addTask( new VfxBallBlockImpactTask( position, material ) );
  }

  public static addIceCrack( position: THREE.Vector3 ): void
  {
    if ( SimulationContext.enabled ) return;

    FpsOverlay.noteEvent( "ice crack" );
    VfxTaskRunner.addTask( new VfxIceSplintersTask( position, IceSplinterType.CRACK ) );
  }

  public static addIceBreak( position: THREE.Vector3, bounds_size?: THREE.Vector3 ): void
  {
    if ( SimulationContext.enabled ) return;

    FpsOverlay.noteEvent( "ice break" );
    VfxTaskRunner.addTask( new VfxIceSplintersTask( position, IceSplinterType.BREAK, bounds_size ) );
  }

  public static addWatermelonBreak( position: THREE.Vector3 ): void
  {
    if ( SimulationContext.enabled ) return;

    FpsOverlay.noteEvent( "watermelon break" );
    VfxTaskRunner.addTask( new VfxWatermelonBreakTask( position ) );
  }

  public static addBigBallBlockImpact( position: THREE.Vector3 ): void
  {
    if ( SimulationContext.enabled ) return;
    
    FpsOverlay.noteEvent( "big ball impact" );
    VfxTaskRunner.addTask( new VfxBigBallBlockImpactTask( position ) );
  }

  public static addJamSplash(
    position: THREE.Vector3,
    half_height: number,
    height_blocks: number,
    jam_set_index: number,
    force_factor: number = 1,
    glass_force_factor: number = 1,
    floor_y: number = 0,
    rotation?: THREE.Quaternion,
  ): void
  {
    if ( SimulationContext.enabled ) return;

    FpsOverlay.noteEvent( "jam" );

    const task = new VfxJamSplashTask( position, half_height, height_blocks, jam_set_index, force_factor, glass_force_factor, floor_y, rotation );
    VfxTaskRunner.addTask( task );
  }

  public static addJamSpill(
    position: THREE.Vector3,
    half_height: number,
    height_blocks: number,
    floor_y: number,
    jam_set_index: number,
    rotation?: THREE.Quaternion,
  ): void
  {
    if ( SimulationContext.enabled ) return;

    FpsOverlay.noteEvent( "jam" );

    const task = new VfxJamSpillTask( position, half_height, height_blocks, floor_y, jam_set_index, rotation );
    VfxTaskRunner.addTask( task );
  }

  public static addBlockShatter(
    position: THREE.Vector3,
    rotation: THREE.Quaternion,
    scale: number | THREE.Vector3,
    texture_id: ResourceID,
    mass: number,
    floor_y: number,
    impact_speed: number,
    grid_size: THREE.Vector2,
  ): void
  {
    if ( SimulationContext.enabled ) return;

    FpsOverlay.noteEvent( "shatter" );

    const task = new VfxBlockShatterTask(
      position,
      rotation,
      scale,
      texture_id,
      mass,
      floor_y,
      impact_speed,
      grid_size
    );
    VfxTaskRunner.addTask( task );
  }

  public static addFireworkTrail( position: THREE.Vector3 ): IController | null
  {
    if ( SimulationContext.enabled ) return null;

    const task = new VfxRocketTrailTask( position );
    VfxTaskRunner.addTask( task );
    return task;
  }

  public static addFireworkExplosion( position: THREE.Vector3 ): void
  {
    if ( SimulationContext.enabled ) return;

    const task = new VfxFireworkExplosionTask( position );
    VfxTaskRunner.addTask( task );
  }
}
